"use client";
import { JourneySteps } from "@/data/journeySteps";
import { cn } from "@/lib/utils";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, MapPin } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

interface Props {
  data: JourneySteps[];
}

export const Timeline = ({ data }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) {
      // eslint-disable-next-line
      const rect = ref.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, [ref]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 70%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div className="w-full container " ref={containerRef}>
      <div ref={ref} className="relative max-w-7xl mx-auto pb-20 ">
        {data?.map((item, index) => (
          <div
            key={index}
            className="flex justify-start pt-10 md:pt-40 md:gap-10"
          >
            <div className="sticky flex flex-col md:flex-row z-10 items-center top-40 self-start max-w-xs lg:max-w-sm ">
              <div className="h-10 absolute left-3 md:left-3 w-10 rounded-full bg-primary/50   flex items-center justify-center">
                <div className="h-4 w-4 rounded-full bg-primary/10  border border-primary/5  p-2" />
              </div>
              <div className="hidden md:block  text-xl md:pl-20 md:text-2xl font-bold text-neutral-500  ">
                {item.role}
              </div>
            </div>

            <div className="relative pl-20 pr-4 md:pl-4 w-full">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="font-semibold text-lg">{item.title}</h2>
                {/* <Badge className={getTypeColor(item.type)}>{item.type}</Badge> */}
              </div>

              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" /> {item.year}
                </div>
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" /> {item.company}
                </div>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {item.description}
              </p>
              <div className={cn(`mt-3 hidden md:grid grid-cols-3 gap-5`)}>
                {item.image.map((img, i) => (
                  <div className="max-w-[300px] w-full relative" key={i}>
                    <Image
                      src={img}
                      alt={item.company}
                      title={item.role}
                      width={300}
                      height={200} // you can dynamically calculate this based on image dimensions
                      className="rounded-xl w-full h-auto"
                      sizes="(max-width: 768px) 100vw, 33vw"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
        <div
          style={{
            height: height + "px",
          }}
          className="absolute md:left-8 left-8 top-0 overflow-hidden w-[2px] 
             bg-[linear-gradient(to_bottom,transparent_30%,#004EB0_90%,transparent_100%)] 
             [mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_50%,transparent_100%)]"
        >
          <motion.div
            className="absolute inset-x-0 top-0 w-[2px] rounded-full"
            // Solid primary color for the moving progress bar
            style={{
              backgroundColor: "#004EB0",
              height: heightTransform,
              opacity: opacityTransform,
            }}
          />
        </div>
      </div>
    </div>
  );
};
